import React, { useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  MenuItem,
  Chip,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningIcon from "@mui/icons-material/Warning";

const AllergiesTab = ({ data, onChange }) => {
  const [newAllergy, setNewAllergy] = useState({
    allergen: "",
    type: "Drug",
    severity: "Mild",
    reaction: "",
    notes: "",
  });

  const allergyTypes = [
    "Drug/Medication",
    "Food",
    "Environmental",
    "Latex",
    "Insect",
    "Other",
  ];

  const severityLevels = ["Mild", "Moderate", "Severe", "Life-threatening"];

  const commonDrugAllergies = [
    "Penicillin",
    "Sulfa drugs",
    "Aspirin",
    "Ibuprofen",
    "Codeine",
    "Morphine",
  ];

  const commonFoodAllergies = [
    "Peanuts",
    "Tree nuts",
    "Shellfish",
    "Fish",
    "Eggs",
    "Milk",
    "Soy",
    "Wheat",
  ];

  const handleAddAllergy = () => {
    if (newAllergy.allergen.trim()) {
      onChange([...(data || []), { ...newAllergy, id: Date.now() }]);
      setNewAllergy({
        allergen: "",
        type: "Drug",
        severity: "Mild",
        reaction: "",
        notes: "",
      });
    }
  };

  const handleRemoveAllergy = (id) => {
    onChange((data || []).filter((allergy) => allergy.id !== id));
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "Mild":
        return "success";
      case "Moderate":
        return "warning";
      case "Severe":
        return "error";
      case "Life-threatening":
        return "error";
      default:
        return "default";
    }
  };

  const hasLifeThreateningAllergy =
    data && data.some((a) => a.severity === "Life-threatening");

  return (
    <Box>
      {hasLifeThreateningAllergy && (
        <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 3 }}>
          <Typography variant="body1" fontWeight="bold">
            CRITICAL: Patient has life-threatening allergies!
          </Typography>
          <Typography variant="body2">
            Review all allergies carefully before prescribing or administering
            any medications.
          </Typography>
        </Alert>
      )}

      <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
        Allergies & Adverse Reactions
      </Typography>

      {/* Add Allergy Form */}
      <Card sx={{ mb: 3, bgcolor: "grey.50" }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Add New Allergy
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Allergen"
                required
                placeholder="Enter allergen name"
                value={newAllergy.allergen}
                onChange={(e) =>
                  setNewAllergy({ ...newAllergy, allergen: e.target.value })
                }
                helperText="What causes the allergic reaction?"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Type"
                required
                value={newAllergy.type}
                onChange={(e) =>
                  setNewAllergy({ ...newAllergy, type: e.target.value })
                }
              >
                {allergyTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Severity"
                required
                value={newAllergy.severity}
                onChange={(e) =>
                  setNewAllergy({ ...newAllergy, severity: e.target.value })
                }
              >
                {severityLevels.map((level) => (
                  <MenuItem key={level} value={level}>
                    {level}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Reaction"
                placeholder="e.g., Hives, Rash, Anaphylaxis"
                value={newAllergy.reaction}
                onChange={(e) =>
                  setNewAllergy({ ...newAllergy, reaction: e.target.value })
                }
                helperText="Describe the allergic reaction"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Additional Notes"
                multiline
                rows={2}
                placeholder="Any additional information about this allergy..."
                value={newAllergy.notes}
                onChange={(e) =>
                  setNewAllergy({ ...newAllergy, notes: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddAllergy}
                fullWidth
              >
                Add Allergy
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Quick Add Buttons */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Common Drug Allergies (Click to add):
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {commonDrugAllergies.map((drug) => (
            <Chip
              key={drug}
              label={drug}
              onClick={() =>
                setNewAllergy({
                  ...newAllergy,
                  allergen: drug,
                  type: "Drug/Medication",
                })
              }
              clickable
              variant="outlined"
              color="primary"
            />
          ))}
        </Box>

        <Typography variant="subtitle2" gutterBottom>
          Common Food Allergies (Click to add):
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {commonFoodAllergies.map((food) => (
            <Chip
              key={food}
              label={food}
              onClick={() =>
                setNewAllergy({ ...newAllergy, allergen: food, type: "Food" })
              }
              clickable
              variant="outlined"
              color="success"
            />
          ))}
        </Box>
      </Box>

      {/* Allergies List */}
      {(!data || data.length === 0) && (
        <Alert severity="success" sx={{ mt: 2 }}>
          No known allergies recorded
        </Alert>
      )}

      {data && data.length > 0 && (
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Recorded Allergies ({data.length})
          </Typography>
          {data.map((allergy) => (
            <Card
              key={allergy.id}
              sx={{
                mb: 2,
                border:
                  allergy.severity === "Life-threatening"
                    ? "2px solid"
                    : "none",
                borderColor: "error.main",
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Typography variant="h6" fontWeight="bold">
                        {allergy.allergen}
                      </Typography>
                      <Chip
                        label={allergy.type}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      <Chip
                        label={allergy.severity}
                        size="small"
                        color={getSeverityColor(allergy.severity)}
                        icon={
                          allergy.severity === "Life-threatening" ? (
                            <WarningIcon />
                          ) : undefined
                        }
                      />
                    </Box>

                    {allergy.reaction && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Reaction:
                        </Typography>
                        <Typography variant="body2">
                          {allergy.reaction}
                        </Typography>
                      </Box>
                    )}

                    {allergy.notes && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Notes:
                        </Typography>
                        <Typography variant="body2">{allergy.notes}</Typography>
                      </Box>
                    )}
                  </Box>

                  <IconButton
                    color="error"
                    onClick={() => handleRemoveAllergy(allergy.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default AllergiesTab;
